import React, { useState, useEffect } from 'react';
import {
  getUsers,
  getUser,
  getDeletedUsers,
  deleteUser,
} from '../services/apiService';

const UserManagement = () => {
  const [activeTab, setActiveTab] = useState('active');
  const [users, setUsers] = useState([]);
  const [deletedUsers, setDeletedUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDetail, setShowDetail] = useState(false);
  const [userDetail, setUserDetail] = useState(null);
  const [detailType, setDetailType] = useState('active'); // 'active' or 'deleted'
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [showPermanentDeleteConfirm, setShowPermanentDeleteConfirm] = useState(false);

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: '', type: 'success' });
    }, 3000);
  };

  const pageSize = 10;

  // 获取已注册用户列表
  useEffect(() => {
    if (activeTab === 'active') {
      fetchUsers();
    }
  }, [activeTab, currentPage]);

  const fetchUsers = async () => {
    setIsLoading(true);
    try {
      const result = await getUsers(currentPage, pageSize);
      if (result.success) {
        setUsers(result.users || []);
        setTotalPages(result.pagination?.pages || 1);
      } else {
        console.error('获取用户列表失败:', result.message);
      }
    } catch (error) {
      console.error('获取用户列表错误:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // 获取已删除用户列表
  const fetchDeletedUsers = async () => {
    setIsLoading(true);
    try {
      const result = await getDeletedUsers(currentPage, pageSize);
      if (result.success) {
        setDeletedUsers(result.deletedUsers || []);
        setTotalPages(result.pagination?.pages || 1);
      } else {
        console.error('获取已删除用户列表失败:', result.message);
      }
    } catch (error) {
      console.error('获取已删除用户列表错误:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // 切换标签页时获取数据
  useEffect(() => {
    if (activeTab === 'deleted') {
      fetchDeletedUsers();
      setCurrentPage(1);
    }
  }, [activeTab]);

  // 删除用户
  const handleDeleteUser = async () => {
    if (!selectedUser) return;

    setDeleteLoading(true);
    try {
      const result = await deleteUser(selectedUser.id, deleteReason);
      if (result.success) {
        showToast('用户已成功删除', 'success');

        // 刷新列表
        fetchUsers();
        setShowDeleteConfirm(false);
        setShowDetail(false);
        setSelectedUser(null);
        setDeleteReason('');
      } else {
        showToast(result.message || '删除失败', 'error');
      }
    } catch (error) {
      console.error('删除用户错误:', error);
      showToast(error.message || '删除失败', 'error');
    } finally {
      setDeleteLoading(false);
    }
  };

  // 打开删除确认对话
  const openDeleteConfirm = (user) => {
    setSelectedUser(user);
    setDeleteReason('');
    setShowDeleteConfirm(true);
  };

  // 查看已删除用户详情
  const handleViewDeletedUserDetail = (user) => {
    setUserDetail(user);
    setDetailType('deleted');
    setShowDetail(true);
  };

  // 查看用户详情
  const handleViewDetail = async (userId) => {
    try {
      const result = await getUser(userId);
      if (result.success) {
        setUserDetail(result.user);
        setDetailType('active');
        setShowDetail(true);
      } else {
        console.error('获取用户详情失败:', result.message);
      }
    } catch (error) {
      console.error('获取用户详情错误:', error);
    }
  };

  // 关闭详情弹窗
  const closeDetail = () => {
    setShowDetail(false);
    setUserDetail(null);
    setDetailType('active');
  };

  // 格式化日期
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('zh-CN');
  };



  // 过滤用户列表
  const filteredUsers = users
    .filter((user) => user.role !== 'admin') // 排除管理员
    .filter(
      (user) =>
        user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (user.full_name &&
          user.full_name.toLowerCase().includes(searchTerm.toLowerCase()))
    );

  return (
    <div className="w-full">
      {/* Toast 提示 */}
      {toast.show && (
        <div className="fixed top-4 right-4 z-[100] animate-in slide-in-from-top-2 fade-in duration-300">
          <div className={`border rounded-lg p-4 flex items-center gap-3 min-w-[300px] ${toast.type === 'success'
            ? 'bg-green-500/20 border-green-500/50'
            : 'bg-red-500/20 border-red-500/50'
            }`}>
            {toast.type === 'success' ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                fill="#22c55e"
                viewBox="0 0 256 256"
              >
                <path d="M229.66,77.66l-128,128a8,8,0,0,1-11.32,0l-64-64a8,8,0,0,1,11.32-11.32L96,188.69,218.34,66.34a8,8,0,0,1,11.32,11.32Z"></path>
              </svg>
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                fill="#ef4444"
                viewBox="0 0 256 256"
              >
                <path d="M128,24A104,104,0,1,0,232,128,104.11,104.11,0,0,0,128,24Zm0,192a88,88,0,1,1,88-88A88.1,88.1,0,0,1,128,216Zm-8-80V80a8,8,0,0,1,16,0v56a8,8,0,0,1-16,0Zm20,36a12,12,0,1,1-12-12A12,12,0,0,1,140,172Z"></path>
              </svg>
            )}
            <div>
              <p className={`${toast.type === 'success' ? 'text-green-300' : 'text-red-300'} font-medium`}>
                {toast.message}
              </p>
              <p className={`${toast.type === 'success' ? 'text-green-400' : 'text-red-400'} text-xs mt-1`}>
                3秒后自动关闭
              </p>
            </div>
            <button
              onClick={() => setToast({ ...toast, show: false })}
              className={`ml-auto ${toast.type === 'success' ? 'text-green-400 hover:text-green-300' : 'text-red-400 hover:text-red-300'}`}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                fill="currentColor"
                viewBox="0 0 256 256"
              >
                <path d="M205.66,194.34a8,8,0,0,1-11.32,11.32L128,139.31,61.66,205.66a8,8,0,0,1-11.32-11.32L116.69,128,50.34,61.66A8,8,0,0,1,61.66,50.34L128,116.69l66.34-66.35a8,8,0,0,1,11.32,11.32L139.31,128Z"></path>
              </svg>
            </button>
          </div>
        </div>
      )}
      {/* 标签页切换 */}
      <div className="flex gap-4 mb-6 border-b border-[#2a2d27]">
        <button
          onClick={() => {
            setActiveTab('active');
            setCurrentPage(1);
            setSearchTerm('');
          }}
          className={`px-4 py-3 font-medium transition-colors border-b-2 ${activeTab === 'active'
            ? 'border-[#f5a623] text-[#f5a623]'
            : 'border-transparent text-gray-400 hover:text-white'
            }`}
        >
          已注册用户
        </button>
        <button
          onClick={() => {
            setActiveTab('deleted');
            setCurrentPage(1);
            setSearchTerm('');
          }}
          className={`px-4 py-3 font-medium transition-colors border-b-2 ${activeTab === 'deleted'
            ? 'border-[#f5a623] text-[#f5a623]'
            : 'border-transparent text-gray-400 hover:text-white'
            }`}
        >
          已注销用户
        </button>
      </div>

      {/* 已注册用户标签页 */}
      {activeTab === 'active' && (
        <div className="space-y-4">
          {/* 搜索框 */}
          <div className="mb-6">
            <input
              type="text"
              placeholder="搜索用户名、邮箱或姓名..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 bg-[#262a22] border border-[#4b5445] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#5a6f4a]"
            />
          </div>

          {/* 用户列表表格 */}
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#f5a623]"></div>
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="bg-[#1a1d17] border border-[#2a2d27] rounded-lg p-8 text-center">
              <p className="text-gray-400">没有找到匹配的用户</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-gray-300">
                <thead className="bg-[#0a0c0a] border-b border-[#2a2d27] sticky top-0">
                  <tr>
                    <th className="px-6 py-3 text-left font-semibold">
                      用户名
                    </th>
                    <th className="px-6 py-3 text-left font-semibold">邮箱</th>
                    <th className="px-6 py-3 text-left font-semibold">姓名</th>
                    <th className="px-6 py-3 text-left font-semibold">国家</th>
                    <th className="px-6 py-3 text-left font-semibold">积分</th>
                    <th className="px-6 py-3 text-left font-semibold">验证</th>
                    <th className="px-6 py-3 text-left font-semibold">
                      注册时间
                    </th>
                    <th className="px-6 py-3 text-center font-semibold">
                      操作
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((user, index) => (
                    <tr
                      key={user.id}
                      className={`border-b border-[#2a2d27] hover:bg-[#1a1d17] transition-colors ${index % 2 === 0 ? 'bg-[#0a0c0a]' : ''
                        }`}
                    >
                      <td className="px-6 py-4 font-medium text-white">
                        {user.username}
                      </td>
                      <td className="px-6 py-4">{user.email}</td>
                      <td className="px-6 py-4">{user.full_name || '-'}</td>
                      <td className="px-6 py-4">{user.country || '-'}</td>
                      <td className="px-6 py-4 text-[#f5a623] font-semibold">
                        {user.total_points || 0}
                      </td>
                      <td className="px-6 py-4">
                        {user.verified ? (
                          <span className="px-3 py-1 bg-green-500/20 text-green-400 text-xs rounded-full">
                            已验证
                          </span>
                        ) : (
                          <span className="px-3 py-1 bg-yellow-500/20 text-yellow-400 text-xs rounded-full">
                            未验证
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-gray-400 text-xs">
                        {formatDate(user.created_at)}
                      </td>
                      <td className="px-6 py-4 text-center space-x-2">
                        <button
                          onClick={() => handleViewDetail(user.id)}
                          className="px-3 py-1 bg-[#4a5f3a] hover:bg-[#5a7f4a] text-white text-xs rounded transition-colors"
                        >
                          详情
                        </button>
                        {user.role !== 'admin' && (
                          <button
                            onClick={() => openDeleteConfirm(user)}
                            className="px-3 py-1 bg-red-600/20 hover:bg-red-600/30 text-red-400 text-xs rounded transition-colors"
                          >
                            删除
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* 分页 */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-6">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="px-3 py-2 bg-[#262a22] hover:bg-[#353b30] disabled:opacity-50 text-white rounded transition-colors"
              >
                上一页
              </button>
              <span className="text-gray-400">
                第 {currentPage} / {totalPages} 页
              </span>
              <button
                onClick={() =>
                  setCurrentPage(Math.min(totalPages, currentPage + 1))
                }
                disabled={currentPage === totalPages}
                className="px-3 py-2 bg-[#262a22] hover:bg-[#353b30] disabled:opacity-50 text-white rounded transition-colors"
              >
                下一页
              </button>
            </div>
          )}
        </div>
      )}

      {/* 已注销用户标签页 */}
      {activeTab === 'deleted' && (
        <div className="space-y-4">
          {/* 搜索框 */}
          <div className="mb-6">
            <input
              type="text"
              placeholder="搜索用户名或邮箱..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 bg-[#262a22] border border-[#4b5445] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#5a6f4a]"
            />
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#f5a623]"></div>
            </div>
          ) : deletedUsers.length === 0 ? (
            <div className="bg-[#1a1d17] border border-[#2a2d27] rounded-lg p-8 text-center">
              <p className="text-gray-400">暂无已注销用户</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-gray-300">
                <thead className="bg-[#0a0c0a] border-b border-[#2a2d27] sticky top-0">
                  <tr>
                    <th className="px-6 py-3 text-left font-semibold">
                      用户名
                    </th>
                    <th className="px-6 py-3 text-left font-semibold">邮箱</th>
                    <th className="px-6 py-3 text-left font-semibold">姓名</th>
                    <th className="px-6 py-3 text-left font-semibold">国家</th>
                    <th className="px-6 py-3 text-left font-semibold">
                      删除原因
                    </th>
                    <th className="px-6 py-3 text-left font-semibold">
                      删除时间
                    </th>
                    <th className="px-6 py-3 text-center font-semibold">
                      操作
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {deletedUsers
                    .filter(
                      (user) =>
                        user.username
                          .toLowerCase()
                          .includes(searchTerm.toLowerCase()) ||
                        user.email
                          .toLowerCase()
                          .includes(searchTerm.toLowerCase())
                    )
                    .map((user, index) => (
                      <tr
                        key={user.id}
                        className={`border-b border-[#2a2d27] ${index % 2 === 0 ? 'bg-[#0a0c0a]' : ''
                          }`}
                      >
                        <td className="px-6 py-4 font-medium text-white">
                          {user.username}
                        </td>
                        <td className="px-6 py-4">{user.email}</td>
                        <td className="px-6 py-4">{user.full_name || '-'}</td>
                        <td className="px-6 py-4">{user.country || '-'}</td>
                        <td className="px-6 py-4">
                          <span className="px-3 py-1 bg-red-500/20 text-red-400 text-xs rounded">
                            {user.reason || '无'}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-gray-400 text-xs">
                          {formatDate(user.deleted_at)}
                        </td>
                        <td className="px-6 py-4 text-center">
                          <button
                            onClick={() => handleViewDeletedUserDetail(user)}
                            className="px-3 py-1 bg-[#4a5f3a] hover:bg-[#5a7f4a] text-white text-xs rounded transition-colors"
                          >
                            详情
                          </button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}

          {/* 分页 */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-6">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="px-3 py-2 bg-[#262a22] hover:bg-[#353b30] disabled:opacity-50 text-white rounded transition-colors"
              >
                上一页
              </button>
              <span className="text-gray-400">
                第 {currentPage} / {totalPages} 页
              </span>
              <button
                onClick={() =>
                  setCurrentPage(Math.min(totalPages, currentPage + 1))
                }
                disabled={currentPage === totalPages}
                className="px-3 py-2 bg-[#262a22] hover:bg-[#353b30] disabled:opacity-50 text-white rounded transition-colors"
              >
                下一页
              </button>
            </div>
          )}
        </div>
      )}

      {/* 用户详情弹窗 */}
      {showDetail && userDetail && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-[#1a1d17] border border-[#4a5f3a] rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* 头部 */}
            <div className="sticky top-0 bg-[#12140f] border-b border-[#2a2d27] px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">用户详情</h2>
              <button
                onClick={closeDetail}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  fill="currentColor"
                  viewBox="0 0 256 256"
                >
                  <path d="M205.66,194.34a8,8,0,0,1-11.32,11.32L128,139.31,61.66,205.66a8,8,0,0,1-11.32-11.32L116.69,128,50.34,61.66A8,8,0,0,1,61.66,50.34L128,116.69l66.34-66.35a8,8,0,0,1,11.32,11.32L139.31,128Z"></path>
                </svg>
              </button>
            </div>

            {/* 内容 */}
            <div className="p-6 space-y-6">
              {/* 用户头像 - 居中显示 */}
              <div className="flex justify-center">
                <div className="w-24 h-24 rounded-full overflow-hidden bg-[#262a22] border-2 border-[#4a5f3a]">
                  {userDetail.avatar_url ? (
                    <img
                      src={userDetail.avatar_url}
                      alt={userDetail.username}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src =
                          'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgdmlld0JveD0iMCAwIDEwMCAxMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjEwMCIgaGVpZ2h0PSIxMDAiIGZpbGw9IiMzMzMiLz48dGV4dCB4PSI1MCIgeT0iNTUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSI0MCIgZmlsbD0iIzY2NiIgdGV4dC1hbmNob3I9Im1pZGRsZSI+P088L3RleHQ+PC9zdmc+';
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="48"
                        height="48"
                        fill="#4a5f3a"
                        viewBox="0 0 256 256"
                      >
                        <path d="M230.92,212c-15.23-26.33-38.7-45.21-66.09-54.16a72,72,0,1,0-73.66,0C63.78,166.78,40.31,185.66,25.08,212a8,8,0,1,0,13.85,8c18.84-32.56,52.14-52,89.07-52s70.23,19.44,89.07,52a8,8,0,1,0,13.85-8ZM72,96a56,56,0,1,1,56,56A56.06,56.06,0,0,1,72,96Z"></path>
                      </svg>
                    </div>
                  )}
                </div>
              </div>

              {/* 基本信息 */}
              <div>
                <h3 className="text-lg font-semibold text-white mb-4">
                  基本信息
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-gray-400 text-sm mb-1">用户名</p>
                    <p className="text-white font-medium">
                      {userDetail.username}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm mb-1">邮箱</p>
                    <p className="text-white font-medium">{userDetail.email}</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm mb-1">姓名</p>
                    <p className="text-white font-medium">
                      {userDetail.full_name || '-'}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm mb-1">性别</p>
                    <p className="text-white font-medium">
                      {userDetail.gender || '-'}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm mb-1">国家</p>
                    <p className="text-white font-medium">
                      {userDetail.country || '-'}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm mb-1">年龄范围</p>
                    <p className="text-white font-medium">
                      {userDetail.age_range || '-'}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm mb-1">首选语言</p>
                    <p className="text-white font-medium">
                      {userDetail.preferred_language === 'zh'
                        ? '中文'
                        : 'English'}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm mb-1">验证状态</p>
                    <p className="text-white font-medium">
                      {userDetail.verified ? '已验证' : '未验证'}
                    </p>
                  </div>
                </div>
              </div>

              {/* 订阅状态 */}
              {detailType === 'active' && (
                <div>
                  <h3 className="text-lg font-semibold text-white mb-4">
                    订阅状态
                  </h3>
                  <div className="space-y-3">
                    {userDetail.subscriptions &&
                      userDetail.subscriptions.length > 0 ? (
                      userDetail.subscriptions.map((sub, index) => {
                        const typeNames = {
                          newsletter: 'Newsletter',
                          promotional: '促销邮件',
                          product_updates: '产品新闻',
                          events: '活动通知',
                        };
                        return (
                          <div
                            key={index}
                            className="flex items-center justify-between p-3 bg-[#262a22] rounded-lg border border-[#4b5445]"
                          >
                            <div>
                              <p className="text-white font-medium">
                                {typeNames[sub.subscription_type] ||
                                  sub.subscription_type}
                              </p>
                              <p className="text-gray-400 text-xs mt-1">
                                订阅于:{' '}
                                {new Date(sub.subscribed_at).toLocaleString(
                                  'zh-CN'
                                )}
                              </p>
                            </div>
                            <div>
                              {sub.is_active ? (
                                <span className="px-3 py-1 bg-green-500/20 text-green-400 text-xs rounded-full">
                                  已订阅
                                </span>
                              ) : (
                                <span className="px-3 py-1 bg-gray-500/20 text-gray-400 text-xs rounded-full">
                                  已取消
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div className="text-center py-6 text-gray-400">
                        该用户暂未订阅任何服务
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* 地址信息 - 仅活跃账户显示 */}
              {detailType === 'active' && userDetail.shipping_address && (
                <div>
                  <h3 className="text-lg font-semibold text-white mb-4">
                    配送地址
                  </h3>
                  <p className="text-gray-300">{userDetail.shipping_address}</p>
                </div>
              )}

              {/* 删除信息 - 仅已删除用户显示 */}
              {detailType === 'deleted' && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-red-400 mb-4">
                    删除信息
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <p className="text-gray-400 text-sm mb-1">删除原因</p>
                      <p className="text-red-300 font-medium">
                        {userDetail.reason || '无'}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm mb-1">删除时间</p>
                      <p className="text-red-300 font-medium">
                        {formatDate(userDetail.deleted_at)}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* 时间信息 */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-gray-400 text-sm mb-1">注册时间</p>
                  <p className="text-gray-300 text-sm">
                    {formatDate(userDetail.created_at)}
                  </p>
                </div>
                <div>
                  <p className="text-gray-400 text-sm mb-1">更新时间</p>
                  <p className="text-gray-300 text-sm">
                    {formatDate(userDetail.updated_at)}
                  </p>
                </div>
              </div>
            </div>

            {/* 底部 */}
            <div className="bg-[#0a0c0a] border-t border-[#2a2d27] px-6 py-4 flex justify-between gap-2">
              <button
                onClick={closeDetail}
                className="px-4 py-2 bg-[#262a22] hover:bg-[#353b30] text-white rounded-lg transition-colors"
              >
                关闭
              </button>
              <div className="flex gap-2">
                {detailType === 'active' && (
                  <button
                    onClick={() => {
                      setSelectedUser(userDetail);
                      setShowDetail(false);
                      openDeleteConfirm(userDetail);
                    }}
                    className="px-4 py-2 bg-red-600/20 hover:bg-red-600/30 text-red-400 rounded-lg transition-colors"
                  >
                    删除用户
                  </button>
                )}

              </div>
            </div>
          </div>
        </div>
      )}



      {/* 删除确认对话 */}
      {showDeleteConfirm && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-[#1a1d17] border-2 border-[#4a5f3a] rounded-lg shadow-2xl max-w-md w-full">
            {/* 头部 */}
            <div className="bg-[#12140f] border-b border-[#2a2d27] px-6 py-4 flex items-center gap-3">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                fill="currentColor"
                viewBox="0 0 256 256"
                className="text-yellow-500"
              >
                <path d="M236.8,188.09,149.35,36.22h0a24.76,24.76,0,0,0-42.7,0L19.2,188.09a23.51,23.51,0,0,0,0,23.72A24.35,24.35,0,0,0,40.55,224h174.9a24.35,24.35,0,0,0,21.33-12.19A23.51,23.51,0,0,0,236.8,188.09ZM222.93,203.8a8.5,8.5,0,0,1-7.48,4.2H40.55a8.5,8.5,0,0,1-7.48-4.2,7.59,7.59,0,0,1,0-7.72L120.52,44.21a8.75,8.75,0,0,1,15,0l87.45,151.87A7.59,7.59,0,0,1,222.93,203.8ZM120,144V104a8,8,0,0,1,16,0v40a8,8,0,0,1-16,0Zm20,36a12,12,0,1,1-12-12A12,12,0,0,1,140,180Z"></path>
              </svg>
              <h2 className="text-xl font-bold text-white">确认删除用户</h2>
            </div>

            {/* 内容 */}
            <div className="p-6 space-y-4">
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                <p className="text-yellow-200 text-sm leading-relaxed">
                  确定要删除用户{' '}
                  <span className="font-bold text-white">{selectedUser.username}</span>{' '}
                  ({selectedUser.email}) 吗？
                </p>
                <p className="text-yellow-300/80 text-xs mt-2">
                  ⚠️ 用户数据将被永久删除,无法恢复。删除记录将保留在"已注销用户"列表中用于审计。
                </p>
              </div>

              <div>
                <label className="block text-gray-400 text-sm font-medium mb-2">
                  删除原因（可选）
                </label>
                <textarea
                  value={deleteReason}
                  onChange={(e) => setDeleteReason(e.target.value)}
                  placeholder="请输入删除原因..."
                  className="w-full px-3 py-2 bg-[#262a22] border border-[#4b5445] rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#5a6f4a] text-sm resize-none"
                  rows="3"
                />
              </div>
            </div>

            {/* 底部 */}
            <div className="bg-[#0a0c0a] border-t border-[#2a2d27] px-6 py-4 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setSelectedUser(null);
                  setDeleteReason('');
                }}
                disabled={deleteLoading}
                className="px-6 py-2 bg-gray-600 hover:bg-gray-700 disabled:opacity-50 text-white rounded-lg transition-colors font-medium"
              >
                取消
              </button>
              <button
                onClick={handleDeleteUser}
                disabled={deleteLoading}
                className="px-6 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white rounded-lg transition-colors font-medium flex items-center gap-2"
              >
                {deleteLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    删除中...
                  </>
                ) : (
                  '确认删除'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
